import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { Site } from '@totum-analytics/db';
import { Button, Card, ErrorNote, Field, Loading, inputClass } from '../components/ui';
import { del, patch, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDay, formatPercent, formatRelative } from '../lib/format';
import { useApi } from '../lib/useApi';
import { useSite } from './SiteLayout';

function Saved({ cacheSynced }: { cacheSynced: boolean }) {
  return (
    <p className="text-sm text-green-700" role="status">
      Saved.{' '}
      {cacheSynced
        ? 'The change reaches the edge within a minute.'
        : 'The edge cache could not be updated just now; it will catch up within five minutes.'}
    </p>
  );
}

/** The same rule as the API: case and spacing do not count. */
const sameName = (a: string, b: string) => a.trim().replace(/\s+/g, ' ').toLowerCase() === b.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Deleting a site (docs/mcp/MANAGING-SITES.md): admins only, a paused site only, its name typed
 * back. It waits 30 days and can be cancelled meanwhile; a site that never had data goes at once.
 */
function DeleteSite({ site, onChanged }: { site: Site; onChanged: () => void }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string } | null>(null);
  const scheduledFor = site.deletion_scheduled_for?.slice(0, 10);

  async function act(run: () => Promise<{ status?: string }>) {
    setBusy(true);
    setError(null);
    try {
      const res = await run();
      if (res.status === 'deleted') navigate('/sites');
      else onChanged();
      setOpen(false);
      setTyped('');
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  }

  if (scheduledFor) {
    return (
      <Card title="Scheduled for deletion">
        <p className="text-sm text-gray-600 mb-4">
          This site and all its data will be deleted on <strong className="text-gray-900">{formatDay(scheduledFor, true)}</strong>. Until then it stays
          paused and nothing is lost. Asked for by {site.deletion_requested_by}.
        </p>
        <Button kind="primary" disabled={busy} onClick={() => void act(() => post(`/sites/${site.id}/cancel-deletion`))}>
          Cancel deletion
        </Button>
        {error && <div className="mt-3"><ErrorNote error={error} /></div>}
      </Card>
    );
  }

  return (
    <Card title="Delete site">
      {site.is_active ? (
        <p className="text-sm text-gray-600">To delete this site, pause it first. Deleting removes every event and report it has.</p>
      ) : !open ? (
        <>
          <p className="text-sm text-gray-600 mb-4">Remove this site and all of its data. It waits 30 days first, and can be cancelled until then.</p>
          <Button kind="danger" onClick={() => setOpen(true)}>Delete site…</Button>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void act(() => del(`/sites/${site.id}`, { confirm_name: typed }));
          }}
        >
          <p className="text-sm text-gray-700 mb-3">
            This deletes <strong>{site.name}</strong> and <strong>all of its data</strong>: every event and every report. It happens in 30 days, and
            can be cancelled until then. {site.first_event_at ? '' : 'This site has never received any data, so it will be deleted at once.'}
          </p>
          <Field label={`Type the site's name, "${site.name}", to confirm`} htmlFor="confirm_name">
            <input id="confirm_name" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" className={inputClass} />
          </Field>
          <div className="flex gap-2 flex-wrap">
            <Button type="submit" kind="danger" disabled={busy || !sameName(typed, site.name)}>
              Delete {site.name}
            </Button>
            <Button type="button" kind="quiet" onClick={() => setOpen(false)}>Keep it</Button>
          </div>
        </form>
      )}
      {error && <div className="mt-3"><ErrorNote error={error} /></div>}
    </Card>
  );
}

export function SiteSettings() {
  const { site, reload } = useSite();
  const { me } = useAuth();
  const [error, setError] = useState<{ message: string } | null>(null);
  const [saved, setSaved] = useState<boolean | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const scheduled = Boolean(site.deletion_scheduled_for);

  async function pauseOrContinue() {
    setError(null);
    setSaved(null);
    try {
      const res = await post<{ cache_synced: boolean }>(`/sites/${site.id}/${site.is_active ? 'pause' : 'resume'}`);
      setSaved(res.cache_synced);
      reload();
    } catch (err) {
      setError(err as Error);
    }
  }

  async function save(body: Partial<Site>) {
    setError(null);
    setSaved(null);
    try {
      const res = await patch<{ cache_synced: boolean }>(`/sites/${site.id}`, body);
      setSaved(res.cache_synced);
      reload();
    } catch (err) {
      setError(err as Error);
    }
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    await save({
      name: String(form.get('name')).trim(),
      hostnames: String(form.get('hostnames')).split(/[\s,]+/).filter(Boolean),
      identity_mode: form.get('identity_mode') as Site['identity_mode'],
    });
  }

  async function rotate() {
    setConfirmRotate(false);
    try {
      const res = await post<{ cache_synced: boolean }>(`/sites/${site.id}/rotate-key`);
      setSaved(res.cache_synced);
      reload();
    } catch (err) {
      setError(err as Error);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card title="Site details" className="lg:col-span-2">
        <form onSubmit={submit} key={site.updated_at}>
          <Field label="Name" htmlFor="name">
            <input id="name" name="name" defaultValue={site.name} required maxLength={120} className={inputClass} />
          </Field>
          <Field label="Hostnames" htmlFor="hostnames" hint="Separate several with spaces or commas. *.example.com covers subdomains but not example.com itself.">
            <input id="hostnames" name="hostnames" defaultValue={site.hostnames.join(', ')} required className={inputClass} />
          </Field>
          <Field
            label="Identity mode"
            htmlFor="identity_mode"
            hint="Identified sites attribute usage to people and accounts. Anonymous sites never collect identities, and any sent are discarded at the edge."
          >
            <select id="identity_mode" name="identity_mode" defaultValue={site.identity_mode} className={inputClass}>
              <option value="identified">Identified — signed-in users of a Totum application</option>
              <option value="anonymous">Anonymous — public website traffic</option>
            </select>
          </Field>
          <div className="flex items-center gap-3 flex-wrap">
            <Button type="submit">Save changes</Button>
            {saved !== null && <Saved cacheSynced={saved} />}
          </div>
          {error && <div className="mt-3"><ErrorNote error={error} /></div>}
        </form>
      </Card>

      <div className="space-y-6">
        <Card title={site.is_active ? 'Pause collecting' : 'Continue collecting'}>
          <p className="text-sm text-gray-600 mb-4">
            {site.is_active
              ? 'Stop accepting events from this site. Nothing already collected is deleted, but anything that arrives while it is paused is lost.'
              : scheduled
                ? 'This site is paused until its deletion is cancelled.'
                : 'This site is paused and is accepting nothing. Continuing does not bring back what arrived meanwhile.'}
          </p>
          <Button kind={site.is_active ? 'danger' : 'primary'} disabled={scheduled} onClick={() => void pauseOrContinue()}>
            {site.is_active ? 'Pause collecting' : 'Continue collecting'}
          </Button>
        </Card>

        {me?.role === 'admin' && <DeleteSite site={site} onChanged={reload} />}

        <Card title="Site key">
          <p className="text-sm text-gray-600 mb-2">
            <span className="font-mono text-xs text-gray-900">{site.site_key}</span>
          </p>
          <p className="text-sm text-gray-600 mb-4">
            The key is public — it is in every page's source, like a Google Analytics ID. Issue a new one only if a site is being
            spammed. Every page carrying the old tag stops reporting until it is updated.
          </p>
          {confirmRotate ? (
            <div className="flex gap-2 flex-wrap">
              <Button kind="danger" onClick={() => void rotate()}>Yes, issue a new key</Button>
              <Button kind="quiet" onClick={() => setConfirmRotate(false)}>Keep the current key</Button>
            </div>
          ) : (
            <Button kind="secondary" disabled={scheduled} onClick={() => setConfirmRotate(true)}>Issue a new key</Button>
          )}
        </Card>
      </div>
    </div>
  );
}

interface Settings {
  environment: string;
  ingest_origin: string;
  processor: { retention_days: number; keeps_raw_events_forever: boolean; published_at: string } | null;
  health_thresholds: { drop_threshold: number; min_baseline: number; quiet_after_days: number };
}

export function GlobalSettings() {
  const { data, error, reload } = useApi<Settings>('/settings');
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  if (!data) return <Loading label="Loading settings" />;
  const p = data.processor;
  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Settings</h1>
      <p className="text-gray-600 mb-6">How this installation is configured. These are set in the Cloudflare dashboard, not here.</p>
      <div className="grid gap-6 md:grid-cols-2">
        <Card title="Raw event retention">
          {p ? (
            <>
              <p className="text-3xl font-semibold text-gray-900 tabular">{p.keeps_raw_events_forever ? 'Kept forever' : `${p.retention_days} days`}</p>
              <p className="mt-2 text-sm text-gray-600">
                {p.keeps_raw_events_forever
                  ? 'RAW_EVENT_RETENTION_DAYS is 0, so raw events are never dropped.'
                  : `Raw events older than this are dropped nightly. Daily summaries are kept indefinitely; they hold no personal data.`}
              </p>
              <p className="mt-2 text-xs text-gray-500">Reported by the processor {formatRelative(p.published_at)}.</p>
            </>
          ) : (
            <p className="text-sm text-gray-600">The processor has not reported its settings yet. It does so every five minutes.</p>
          )}
        </Card>
        <Card title="Account health thresholds">
          <dl className="text-sm grid grid-cols-[1fr_auto] gap-y-2">
            <dt className="text-gray-600">A drop of at least</dt>
            <dd className="text-gray-900 tabular text-right">{formatPercent(data.health_thresholds.drop_threshold)}</dd>
            <dt className="text-gray-600">From a baseline of at least</dt>
            <dd className="text-gray-900 tabular text-right">{data.health_thresholds.min_baseline} users</dd>
            <dt className="text-gray-600">Gone quiet after</dt>
            <dd className="text-gray-900 tabular text-right">{data.health_thresholds.quiet_after_days} days</dd>
          </dl>
        </Card>
        <Card title="Environment">
          <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            <dt className="text-gray-600">Environment</dt>
            <dd className="text-gray-900">{data.environment}</dd>
            <dt className="text-gray-600">Tracker served from</dt>
            <dd className="text-gray-900 font-mono text-xs break-all">{data.ingest_origin}</dd>
          </dl>
        </Card>
      </div>
    </>
  );
}
